import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Pencil, Trash2, Phone, Mail, MapPin, ArrowRight, ArrowLeft, Copy, Calendar, Clock, ClipboardList } from "lucide-react";
import Seo from "@/lib/seo";
import api from "@/lib/api";

const STAGES = [
  { key: "nouveau", label: "Projet créé" },
  { key: "rdv_prevu", label: "Rendez-vous fixé" },
  { key: "visite_effectuee", label: "Visite effectuée" },
  { key: "devis_envoye", label: "Devis envoyé" },
  { key: "accepte", label: "Devis accepté" },
  { key: "chantier_en_cours", label: "Chantier en cours" },
  { key: "termine", label: "Chantier terminé" },
];

function stageIndex(status) {
  const map = {
    nouveau: 0, a_rappeler: 0,
    rdv_prevu: 1,
    visite_effectuee: 2, a_chiffrer: 2,
    devis_envoye: 3,
    accepte: 4, refuse: 4,
    chantier_en_cours: 5,
    termine: 6,
  };
  return map[status] ?? 0;
}

export default function ProjectPage() {
  const { accessToken } = useParams();
  const nav = useNavigate();
  const [project, setProject] = useState(null);
  const [appointment, setAppointment] = useState(null);

  const load = () => {
    api.get(`/projects/${accessToken}`).then(({ data }) => {
      setProject(data);
      if (data.appointment_id) {
        api.get(`/appointments/by-project/${data.id}`).then(({ data: a }) => setAppointment(a.appointment)).catch(() => {});
      } else {
        setAppointment(null);
      }
    }).catch(() => toast.error("Projet introuvable"));
  };

  useEffect(() => { load(); }, [accessToken]);

  if (!project) return (
    <div className="pt-[100px] md:pt-[140px] pb-24 wrap"><div className="font-mono-caption text-ink-soft">Chargement…</div></div>
  );

  const idx = stageIndex(project.status);
  const est = project.estimation;

  const cancelRdv = async () => {
    if (!project.appointment_id) return;
    if (!confirm("Annuler le rendez-vous ? Vous pourrez en prendre un nouveau.")) return;
    try {
      await api.delete(`/appointments/${project.appointment_id}`);
      toast.success("Rendez-vous annulé");
      load();
    } catch { toast.error("Erreur"); }
  };

  const copyRef = () => {
    navigator.clipboard?.writeText(project.ref);
    toast.success("Numéro copié");
  };

  return (
    <div className="pt-[100px] md:pt-[140px] pb-24">
      <Seo title="Espace projet | RAMA DÉCO" description="Suivi privé de votre chantier." path="/projet" noindex />
      <div className="wrap max-w-5xl">
        {/* En-tête */}
        <div className="flex flex-wrap items-start justify-between gap-6 mb-10">
          <div>
            <div className="font-mono-caption text-bordeaux-700 mb-3">Espace projet</div>
            <div className="flex items-center gap-3">
              <h1 className="font-display text-3xl md:text-5xl tracking-tight text-anthracite">Projet <span className="italic text-bordeaux-700">{project.ref}</span></h1>
              <button onClick={copyRef} aria-label="Copier" className="p-2 rounded hover:bg-anthracite/5 text-ink-soft"><Copy size={16} /></button>
            </div>
            <p className="mt-2 text-sm text-ink-soft">Créé le {new Date(project.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</p>
          </div>
          <Link to="/mon-projet" className="inline-flex items-center gap-2 text-sm text-ink-soft hover:text-anthracite">
            <ArrowLeft size={14} /> Changer de projet
          </Link>
        </div>

        {/* Barre d'avancement premium */}
        <div data-testid="progress-tracker" className="bg-white border border-black/10 rounded-rama-lg p-6 md:p-10 mb-10">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="font-mono-caption text-ink-soft">Avancement</div>
              <div className="font-display text-xl md:text-2xl text-anthracite mt-1">{STAGES[idx].label}</div>
            </div>
            <div className="text-right">
              <div className="font-mono-caption text-ink-soft">Étape</div>
              <div className="font-display text-xl md:text-2xl text-bordeaux-700 mt-1">{idx + 1} <span className="text-ink-soft text-base">/ {STAGES.length}</span></div>
            </div>
          </div>

          {/* Ligne d'étapes */}
          <div className="relative">
            {/* Ligne de fond */}
            <div className="absolute top-4 left-0 right-0 h-[2px] bg-anthracite/10" aria-hidden />
            {/* Ligne remplie */}
            <div
              className="absolute top-4 left-0 h-[2px] bg-bordeaux-700 transition-all duration-700"
              style={{ width: `${(idx / (STAGES.length - 1)) * 100}%` }}
              aria-hidden
            />
            <ol className="relative grid grid-cols-7 gap-1">
              {STAGES.map((s, i) => {
                const done = i < idx;
                const current = i === idx;
                return (
                  <li key={s.key} className="flex flex-col items-center gap-3 text-center">
                    <div className={`relative z-10 w-8 h-8 md:w-9 md:h-9 rounded-full flex items-center justify-center transition-all ${
                      done ? "bg-bordeaux-700 text-ivory" :
                      current ? "bg-bordeaux-700 text-ivory ring-4 ring-bordeaux-700/15 scale-110" :
                      "bg-ivory border-2 border-anthracite/15 text-ink-soft"
                    }`}>
                      {done ? <Check size={16} strokeWidth={2.5} /> : <span className="font-mono-caption text-[0.6rem]">{String(i + 1).padStart(2, "0")}</span>}
                    </div>
                    <div className={`hidden md:block text-[0.68rem] leading-tight px-1 ${current ? "text-anthracite font-semibold" : done ? "text-ink-soft" : "text-ink-soft"}`}>{s.label}</div>
                  </li>
                );
              })}
            </ol>
            {/* Legend mobile */}
            <div className="md:hidden mt-4 grid grid-cols-2 gap-y-2 text-xs">
              {idx > 0 && <div className="text-ink-soft">Précédent : {STAGES[idx - 1].label}</div>}
              {idx < STAGES.length - 1 && <div className="text-anthracite font-semibold text-right">Prochain : {STAGES[idx + 1].label}</div>}
            </div>
          </div>
        </div>

        {/* Grille principale */}
        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {/* Estimation */}
          <div className="md:col-span-2 space-y-6">
            {est ? (
              <div className="bg-anthracite text-ivory rounded-rama-md p-6 md:p-8" data-testid="project-estimation">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <div className="font-mono-caption text-ivory/60 mb-1">
                      {est.source === "deep" ? "Étude approfondie" : "Estimation immédiate"}
                    </div>
                    <div className="font-display text-3xl md:text-4xl leading-tight" data-testid="project-estimation-range">
                      {Math.round(est.total_min).toLocaleString("fr-FR")} — {Math.round(est.total_max).toLocaleString("fr-FR")} €
                      <span className="text-lg"> {est.unite || "TTC"}</span>
                    </div>
                    <div className="text-xs text-ivory/60 mt-1">
                      Budget central ≈ {Math.round(est.total_ref).toLocaleString("fr-FR")} € {est.unite || "TTC"}
                      {est.total_ref_ttc ? ` · soit ${Math.round(est.total_ref_ttc).toLocaleString("fr-FR")} € TTC` : ""}
                    </div>
                  </div>
                  <Link to={`/estimation/${project.id}`} data-testid="edit-project" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-rama-sm bg-ivory/10 hover:bg-ivory/20 text-ivory text-xs font-semibold">
                    <Pencil size={12} /> Modifier
                  </Link>
                </div>
                <div className="pt-6 border-t border-ivory/15 space-y-2">
                  {(est.postes || []).map((p, i) => (
                    <div key={`${p.famille}-${i}`} className="flex justify-between text-sm">
                      <span className="text-ivory/80">{p.famille}</span>
                      <span>≈ {Math.round(p.montant).toLocaleString("fr-FR")} €</span>
                    </div>
                  ))}
                </div>
                <p className="mt-6 pt-6 border-t border-ivory/15 text-xs text-ivory/60 leading-relaxed">
                  Estimation indicative, non contractuelle. Prochaine étape : la visite technique, qui donne lieu au
                  devis définitif.
                </p>
              </div>
            ) : (
              <div className="bg-white border border-dashed border-anthracite/20 rounded-rama-md p-6 md:p-8">
                <div className="font-mono-caption text-ink-soft mb-2">Estimation</div>
                <p className="text-ink-soft mb-4">Votre projet n'est pas encore chiffré.</p>
                <Link to={`/estimation/${project.id}`} className="btn-primary">Chiffrer mon projet <ArrowRight size={16} /></Link>
              </div>
            )}

            {/* Prestations */}
            <div className="bg-white border border-black/10 rounded-rama-md p-6 md:p-8">
              <div className="flex items-center justify-between mb-4">
                <div className="font-display text-xl text-anthracite">Prestations</div>
                <Link to={`/estimation/${project.id}`} className="text-xs font-semibold text-bordeaux-700 hover:underline inline-flex items-center gap-1"><Pencil size={12} /> Modifier</Link>
              </div>
              {project.prestations?.length ? (
                <ul className="space-y-2.5">
                  {project.prestations.map((p, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm">
                      <Check size={16} className="text-bordeaux-700 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-anthracite">{p.nom_client}</div>
                        <div className="text-xs text-ink-soft">{p.famille}{p.quantite_estimee ? ` · ≈ ${p.quantite_estimee} ${p.unite_probable}` : ""}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-ink-soft">Aucune prestation.</p>
              )}
            </div>
          </div>

          {/* Contact + RDV */}
          <div className="space-y-6">
            {/* Rendez-vous */}
            <div data-testid="rdv-card" className="bg-white border border-black/10 rounded-rama-md p-6">
              <div className="flex items-center justify-between mb-3">
                <div className="font-display text-lg text-anthracite">Rendez-vous</div>
                {project.appointment_id && (
                  <button onClick={cancelRdv} data-testid="cancel-rdv" className="text-ink-soft hover:text-bordeaux-700 p-1" title="Annuler"><Trash2 size={14} /></button>
                )}
              </div>
              {project.appointment_id && project.status === "rdv_prevu" ? (
                <>
                  <div className="text-sm space-y-2 text-anthracite">
                    {appointment ? (
                      <>
                        <div data-testid="rdv-date" className="flex items-center gap-2"><Calendar size={14} className="text-bordeaux-700" /> {new Date(appointment.date).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}</div>
                        <div data-testid="rdv-creneau" className="flex items-center gap-2"><Clock size={14} className="text-bordeaux-700" /> {appointment.creneau}</div>
                        <div data-testid="rdv-type" className="text-xs text-ink-soft mt-2">{appointment.type_rdv === "appel" ? "Appel téléphonique" : "Visite technique sur site"}</div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center gap-2"><Calendar size={14} className="text-bordeaux-700" /> RDV planifié</div>
                        <div className="text-xs text-ink-soft">Chargement du détail…</div>
                      </>
                    )}
                  </div>
                  <Link to={`/rdv/${project.id}`} data-testid="reprogrammer-rdv" className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-bordeaux-700 link-underline">
                    <Pencil size={12} /> Reprogrammer
                  </Link>
                </>
              ) : (
                <>
                  <p className="text-sm text-ink-soft mb-4">Aucun rendez-vous prévu.</p>
                  <Link to={`/rdv/${project.id}`} data-testid="book-rdv" className="btn-primary text-xs px-4 py-2.5">Planifier un rendez-vous <ArrowRight size={14} /></Link>
                </>
              )}
            </div>

            {/* Contact */}
            {project.contact?.nom && (
              <div className="bg-white border border-black/10 rounded-rama-md p-6">
                <div className="font-display text-lg text-anthracite mb-3">Coordonnées</div>
                <div className="text-sm text-ink-soft space-y-2">
                  <div className="text-anthracite font-medium">{project.contact.nom} {project.contact.prenom}</div>
                  {project.contact.telephone && <div className="flex items-center gap-2"><Phone size={14} /> {project.contact.telephone}</div>}
                  {project.contact.email && <div className="flex items-center gap-2 break-all"><Mail size={14} /> {project.contact.email}</div>}
                  {project.contact.adresse && <div className="flex items-start gap-2"><MapPin size={14} className="mt-0.5 flex-shrink-0" /> {project.contact.adresse}</div>}
                </div>
              </div>
            )}

            {/* Contact RAMA */}
            <div className="bg-ivory border border-black/10 rounded-rama-md p-6">
              <div className="font-mono-caption text-ink-soft mb-2">Une question ?</div>
              <a href="tel:0627443230" className="btn-ghost w-full justify-center"><Phone size={14} /> 06 27 44 32 30</a>
            </div>
          </div>
        </div>

        {/* Description initiale */}
        {project.description && (
          <details className="mt-10 bg-white border border-black/10 rounded-rama-md p-6 md:p-8">
            <summary className="cursor-pointer font-display text-lg text-anthracite flex items-center gap-2">
              <ClipboardList size={16} className="text-bordeaux-700" /> Description initiale
            </summary>
            <p className="mt-4 text-sm text-ink-soft leading-relaxed whitespace-pre-line">{project.description}</p>
          </details>
        )}
      </div>
    </div>
  );
}
